import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/AuthContext";
import { ThemeProvider } from "@/ThemeContext";
import Login from "@/pages/Login";
import AdminLayout from "@/pages/AdminLayout";
import Dashboard from "@/pages/Dashboard";
import Loja from "@/pages/Loja";
import Produtos from "@/pages/Produtos";
import Clientes from "@/pages/Clientes";
import Vendas from "@/pages/Vendas";
import Pedidos from "@/pages/Pedidos";
import Historico from "@/pages/Historico";
import CatalogoAdmin from "@/pages/CatalogoAdmin";
import Financeiro from "@/pages/Financeiro";
import Despesas from "@/pages/Despesas";
import PublicCatalog from "@/pages/PublicCatalog";
import Master from "@/pages/Master";
import MasterNotifications from "@/pages/MasterNotifications";
import MasterCRM from "@/pages/MasterCRM";
import Comparador from "@/pages/Comparador";
import LandingAdmin from "@/pages/LandingAdmin";
import CRM from "@/pages/CRM";
import Crediario from "@/pages/Crediario";
import Tutoriais from "@/pages/Tutoriais";
import MasterTutoriais from "@/pages/MasterTutoriais";
import MasterEvaluator from "@/pages/MasterEvaluator";
import Avaliador from "@/pages/Avaliador";
import SimuladorTaxas from "@/pages/SimuladorTaxas";
import Comissoes from "@/pages/Comissoes";
import Funcionarios from "@/pages/Funcionarios";
import Conta from "@/pages/Conta";
import MeuPlano from "@/pages/MeuPlano";
import ResetPassword from "@/pages/ResetPassword";
import Assistencia from "@/pages/Assistencia";
import ServiceTracking from "@/pages/ServiceTracking";
import Pagamento from "@/pages/Pagamento";
import PagamentoParcela from "@/pages/PagamentoParcela";
import PublicBalance from "@/pages/PublicBalance";
import PayMaster from "@/pages/PayMaster";
import PublicSignup from "@/pages/PublicSignup";
import StripePayReturn from "@/pages/StripePayReturn";
import PublicPay from "@/pages/PublicPay";
import LandingRedirect from "@/pages/LandingRedirect";
import Landing from "@/pages/Landing";
import ErrorBoundary from "@/components/ErrorBoundary";
import LoadingBar from "@/components/LoadingBar";
import { HiddenValuesProvider } from "@/contexts/HiddenValues";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500">Carregando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "master" && !user.impersonating) return <Navigate to="/master" replace />;
  if (user.role === "tecnico") return <Navigate to="/assistencia" replace />;
  if (user.role === "marketing_crm") return <Navigate to="/crm" replace />;
  return <Dashboard />;
}

// Bloqueia rotas restritas para o tecnico (só pode usar Assistência + Conta)
function TecnicoBlock({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && user.role === "tecnico") return <Navigate to="/assistencia" replace />;
  // marketing_crm: permitido APENAS no /crm (e em /tutoriais, /conta sem TecnicoBlock).
  // As outras rotas envolvem este TecnicoBlock e devem redirecionar de volta.
  if (user && user.role === "marketing_crm") return <Navigate to="/crm" replace />;
  return children;
}

// Permite acesso ao CRM SEM o redirect do TecnicoBlock (usado SÓ na rota /crm).
function CRMAccessBlock({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && user.role === "tecnico") return <Navigate to="/assistencia" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <LoadingBar />
      <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
        <HiddenValuesProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/catalogo/:slug" element={<ErrorBoundary><PublicCatalog /></ErrorBoundary>} />
            <Route path="/servico/:token" element={<ErrorBoundary><ServiceTracking /></ErrorBoundary>} />
            <Route path="/pagamento/:token" element={<ErrorBoundary><Pagamento /></ErrorBoundary>} />
            <Route path="/pagamento-parcela/:token" element={<ErrorBoundary><PagamentoParcela /></ErrorBoundary>} />
            <Route path="/extrato/:token" element={<ErrorBoundary><PublicBalance /></ErrorBoundary>} />
            <Route path="/pagar/master" element={<ErrorBoundary><PayMaster /></ErrorBoundary>} />
            <Route path="/pagar/master/sucesso" element={<ErrorBoundary><StripePayReturn kind="success"/></ErrorBoundary>} />
            <Route path="/pagar/master/cancelado" element={<ErrorBoundary><StripePayReturn kind="cancel"/></ErrorBoundary>} />
            <Route path="/pagar/stripe/:token" element={<ErrorBoundary><PublicPay /></ErrorBoundary>} />
            <Route path="/cadastro" element={<ErrorBoundary><PublicSignup /></ErrorBoundary>} />
            <Route path="/landingpage" element={<Landing />} />
            <Route path="/landing-canva" element={<LandingRedirect />} />
            <Route path="/" element={<Protected><AdminLayout /></Protected>}>
              <Route index element={<ErrorBoundary><RootRedirect /></ErrorBoundary>} />
              <Route path="master" element={<ErrorBoundary><Master /></ErrorBoundary>} />
              <Route path="master/notificacoes" element={<ErrorBoundary><MasterNotifications /></ErrorBoundary>} />
              <Route path="master/crm" element={<ErrorBoundary><MasterCRM /></ErrorBoundary>} />
              <Route path="master/landing" element={<ErrorBoundary><LandingAdmin /></ErrorBoundary>} />
              <Route path="master/tutoriais" element={<ErrorBoundary><MasterTutoriais /></ErrorBoundary>} />
              <Route path="master/avaliador" element={<ErrorBoundary><MasterEvaluator /></ErrorBoundary>} />
              <Route path="avaliador" element={<ErrorBoundary><TecnicoBlock><Avaliador /></TecnicoBlock></ErrorBoundary>} />
              <Route path="loja" element={<ErrorBoundary><TecnicoBlock><Loja /></TecnicoBlock></ErrorBoundary>} />
              <Route path="produtos" element={<ErrorBoundary><TecnicoBlock><Produtos /></TecnicoBlock></ErrorBoundary>} />
              <Route path="clientes" element={<ErrorBoundary><TecnicoBlock><Clientes /></TecnicoBlock></ErrorBoundary>} />
              <Route path="vendas" element={<ErrorBoundary><TecnicoBlock><Vendas /></TecnicoBlock></ErrorBoundary>} />
              <Route path="simulador-taxas" element={<ErrorBoundary><TecnicoBlock><SimuladorTaxas /></TecnicoBlock></ErrorBoundary>} />
              <Route path="comissoes" element={<ErrorBoundary><TecnicoBlock><Comissoes /></TecnicoBlock></ErrorBoundary>} />
              <Route path="crm" element={<ErrorBoundary><CRMAccessBlock><CRM /></CRMAccessBlock></ErrorBoundary>} />
              <Route path="crediario" element={<ErrorBoundary><TecnicoBlock><Crediario /></TecnicoBlock></ErrorBoundary>} />
              <Route path="tutoriais" element={<ErrorBoundary><Tutoriais /></ErrorBoundary>} />
              <Route path="pedidos" element={<ErrorBoundary><TecnicoBlock><Pedidos /></TecnicoBlock></ErrorBoundary>} />
              <Route path="historico" element={<ErrorBoundary><TecnicoBlock><Historico /></TecnicoBlock></ErrorBoundary>} />
              <Route path="catalogo-admin" element={<ErrorBoundary><TecnicoBlock><CatalogoAdmin /></TecnicoBlock></ErrorBoundary>} />
              <Route path="financeiro" element={<ErrorBoundary><TecnicoBlock><Financeiro /></TecnicoBlock></ErrorBoundary>} />
              <Route path="financeiro/despesas" element={<ErrorBoundary><TecnicoBlock><Despesas /></TecnicoBlock></ErrorBoundary>} />
              <Route path="assistencia" element={<ErrorBoundary><Assistencia /></ErrorBoundary>} />
              <Route path="comparador" element={<ErrorBoundary><TecnicoBlock><Comparador /></TecnicoBlock></ErrorBoundary>} />
              <Route path="funcionarios" element={<ErrorBoundary><TecnicoBlock><Funcionarios /></TecnicoBlock></ErrorBoundary>} />
              <Route path="conta" element={<ErrorBoundary><Conta /></ErrorBoundary>} />
              <Route path="meu-plano" element={<ErrorBoundary><MeuPlano /></ErrorBoundary>} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </HiddenValuesProvider>
      </AuthProvider>
      </ThemeProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
